// Booking types
export type BookingStatus =
  | 'quote'
  | 'pending'
  | 'vehicle_arrived'
  | 'in_progress'
  | 'paused'
  | 'work_completed'
  | 'ready_to_invoice'
  | 'invoiced'
  | 'completed'
  | 'cancelled'
  | 'quote_rejected'
  // Legacy values — used by calendar/kanban/toolbar/history, kept for backward compatibility
  | 'confirmed'       // legacy: confirmation is now the isConfirmed flag (#534)
  | 'scheduled'
  | 'waiting_approval'
  | 'waiting_parts'
  | 'warranty_claim'  // legacy: now isWarranty flag
  | 'disputed'        // legacy: now isDisputed flag
  | 'invoicing_hold'; // legacy: now isInvoicingHold flag

export interface Booking {
  id: string;
  customerId: string;
  customerName: string;
  customerPhone: string;
  vehicleId: string;
  vehicleRegNumber: string;
  vehicleMakeModel: string;
  mechanicId: string;
  mechanicName: string;
  scheduledStart: string; // ISO date string
  scheduledEnd: string;
  status: BookingStatus;
  // Confirmation is a boolean flag, not a primary status (#534). Optional so
  // existing construction sites keep compiling until callers map the API field.
  isConfirmed?: boolean;
  services: BookingService[];
  estimatedTotalCents: number;
  notes?: string;
  assignedMechanicIds: string[];
  hasUnassignedServices: boolean;
  // Pre-approved additional work limit (#66)
  preapprovedAdditionalWorkLimitCents?: number | null;
}

export interface BookingService {
  id: string;
  name: string;
  estimatedHours: number;
  priceCents: number;
  assignedMechanicId?: string | null;
  mechanicName?: string | null;
}

// Mechanic types
export interface Mechanic {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  avatarUrl?: string;
  skills: string[];
  isAvailable: boolean;
}

// Small supplies surcharge (matches backend surcharge.schema.ts)
export type SurchargeType = 'labor' | 'parts' | 'total' | 'fixed';

export interface SmallSuppliesSurcharge {
  type: SurchargeType;
  percentage?: number | null;
  fixedCents?: number | null;
}

// Customer types
export type CustomerType = 'individual' | 'business';

export interface Customer {
  id: string;
  customerType?: CustomerType;
  businessName?: string | null;
  businessId?: string | null;
  displayName?: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  avatarUrl?: string;
}

export interface CustomerDetails extends Customer {
  address?: string;
  city?: string;
  postalCode?: string;
  notes?: string;
  vehicleCount: number;
  totalSpentCents: number;
  visitCount: number;
  lastVisitAt?: string;
  createdAt: string;
  preferredContact: 'phone' | 'email' | 'whatsapp';
  smallSuppliesSurcharge?: SmallSuppliesSurcharge | null;
}

// Vehicle types
export interface Vehicle {
  id: string;
  customerId: string;
  registrationNumber: string;
  make: string;
  model: string;
  year: number;
  vin?: string;
  mileage?: number;
}

// Vehicle-customer role types
export type VehicleCustomerRole = 'owner' | 'holder' | 'work_orderer';

export interface VehicleCustomerLink {
  id: string;
  vehicleId: string;
  customerId: string;
  role: VehicleCustomerRole;
  startedAt: string;
  endedAt: string | null;
  customerFirstName: string;
  customerLastName: string;
  customerPhone: string | null;
  customerEmail: string | null;
  customerType: string;
  businessName: string | null;
}

export interface VehicleDetails extends Vehicle {
  customer: Customer;
  owner?: VehicleCustomerLink | null;
  holder?: VehicleCustomerLink | null;
  workOrderers?: VehicleCustomerLink[];
  color?: string;
  engineType?: 'petrol' | 'diesel' | 'hybrid' | 'electric';
  transmission?: 'manual' | 'automatic';
  fuelType?: string;
  lastServiceAt?: string;
  nextServiceDue?: string;
  motExpiryDate?: string; // Katsastus
  insuranceExpiryDate?: string;
  notes?: string;
  serviceCount: number;
  totalSpentCents: number;
  createdAt: string;
  traficomData?: TraficomData;
  /** Hand-corrected registry fields (#2337). Display: override ?? traficomData. */
  manualOverrides?: Partial<TraficomData> | null;
}

// Traficom (Finnish vehicle registry) data
export interface TraficomData {
  // Vehicle identification
  registrationNumber: string;
  vin: string;
  vehicleClass: string; // M1, N1, etc.
  vehicleGroup: string; // Henkilöauto, Pakettiauto, etc.

  // Technical specifications
  make: string;
  model: string;
  modelYear: number;
  bodyType: string; // Sedan, Hatchback, Farmari, etc.
  color: string;
  doors: number;
  seats: number;

  // Engine & Performance
  engineDisplacementCc: number;
  enginePowerKw: number;
  fuelType: string; // Bensiini, Diesel, Sähkö, Hybridi
  transmission: string;
  driveType: string; // Etu, Taka, Neli

  // Dimensions & Weight
  lengthMm: number;
  widthMm: number;
  heightMm: number;
  curbWeightKg: number;
  grossWeightKg: number;

  // Emissions & Environment
  co2EmissionsGkm?: number;
  euroEmissionClass?: string; // Euro 6, Euro 6d-TEMP, etc.
  combinedFuelConsumptionL100km?: number;

  // Registration info
  firstRegistrationDate: string;
  firstRegistrationDateFinland?: string;
  registrationStatus: string; // Liikennekäytössä, Liikennekäytöstä poistettu
  ownershipType: string; // Omistus, Hallinta

  // Inspection (Katsastus) info
  lastInspectionDate?: string;
  nextInspectionDue?: string;
  inspectionResult?: 'approved' | 'approved_with_notes' | 'rejected';
  inspectionNotes?: string[];

  // Tax & Insurance
  vehicleTaxEurPerYear?: number;
  insuranceClass?: string;

  // Tires
  tireSizeFront?: string;
  tireSizeRear?: string;
  rimSize?: string;

  // Metadata
  fetchedAt: string; // When the data was last fetched from Traficom
}

export interface ServiceHistoryEntry {
  id: string;
  workOrderNumber: string;
  date: string;
  mileage: number;
  services: string[];
  totalCents: number;
  mechanicName: string;
  notes?: string;
}

// Work order types
export interface WorkOrder {
  requiresStaffReview?: boolean;
  id: string;
  workOrderNumber: string;
  bookingId?: string;
  customerId: string;
  customer: Customer;
  contactPerson?: {
    id: string;
    firstName: string;
    lastName: string | null;
  } | null;
  vehicleId: string;
  vehicle: Vehicle;
  mechanicId?: string;
  mechanic?: Mechanic;
  status: BookingStatus;
  /** Backend status as received; may be a legacy or unknown key (#2918). */
  rawStatus?: string;
  /** Planned hours cache from the booking (#2707). */
  estimatedDurationHours?: number | null;
  /** Closed clocked hours; open timers are not included (#2707). */
  actualDurationHours?: number;
  services: WorkOrderService[];
  /** VAT-INCLUSIVE parts roll-up, so it reconciles with the printed part lines. */
  totalPartsCents: number;
  /** VAT-INCLUSIVE labor roll-up, so it reconciles with the printed labor lines. */
  totalLaborCents: number;
  /** Taxable base (net). Shown as "Veroton yhteensä" (docs/VAT-BASIS.md § 5). */
  totalCents: number;
  // VAT breakdown for the printout (#843). totalCents is the net total; the
  // fields below carry the correct rate and amount from the API.
  vatPercent?: number;
  taxCents?: number;
  totalWithVatCents?: number;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  customerWaits?: boolean;
  allowMinorMaintenance?: boolean;
  // Flags (orthogonal to status)
  isWarranty?: boolean;
  isDisputed?: boolean;
  isInvoicingHold?: boolean;
  // Pre-approved additional work limit (#66)
  preapprovedAdditionalWorkLimitCents?: number | null;
  isWaitingForParts?: boolean;
  /** Latest customer-reported vehicle dropoff (PF-135). Absent or null when none. */
  dropoff?: WorkOrderDropoff | null;
}

/** Customer-reported after-hours dropoff on a work order (PF-135). */
export interface WorkOrderDropoff {
  reportedAt: string;
  keysLocation: string | null;
  comment: string | null;
  autoArrived: boolean;
  acknowledgedAt: string | null;
  acknowledgedBy: string | null;
}

export interface WorkOrderService {
  id: string;
  name: string;
  description?: string;
  status: 'pending' | 'in_progress' | 'paused' | 'completed';
  parts: WorkOrderPart[];
  laborHours: number;
  laborPriceCents: number;
}

export interface WorkOrderPart {
  id: string;
  partNumber: string;
  name: string;
  quantity: number;
  unit: string;
  unitPriceCents: number;
  totalPriceCents: number;
}

export type NoteCategory = 'customer' | 'internal' | 'manager';

export interface WorkOrderNote {
  id: string;
  bookingId: string;
  category: NoteCategory;
  content: string;
  authorId: string;
  authorName: string | null;
  createdAt: string;
  /** ISO timestamp of last edit, or null if never edited (#550). */
  updatedAt: string | null;
  /** Highlighted note (#2343). Optional so older API responses still type-check. */
  isPinned?: boolean;
  pinnedAt?: string | null;
  pinnedBy?: string | null;
}

export type WorkOrderHistoryAction =
  | 'created'
  | 'status_changed'
  | 'mechanic_assigned'
  | 'note_added'
  | 'item_added'
  | 'item_removed'
  | 'approval_requested'
  | 'approval_received'
  | 'approval_declined'
  | 'customer_contacted'
  | 'parts_ordered'
  | 'inspection_completed';

export interface StatusHistoryEntry {
  id: string;
  oldStatus: string;
  newStatus: string;
  changedBy: string | null;
  changedByName: string | null;
  /** Structured reason for a non-status field change, e.g. labor_rate_cents:9500:8000 (#1437). */
  reasonForChange?: string | null;
  createdAt: string;
}

export interface WorkOrderHistoryEntry {
  id: string;
  workOrderId: string;
  action: WorkOrderHistoryAction;
  description: string;
  metadata?: Record<string, unknown>;
  userId: string;
  userName: string;
  createdAt: string;
}

// Per-service calendar item — one rendered segment per (booking_service × shift)
export interface CalendarServiceItem {
  id: string;                    // unique segment id: `${sourceId}#${segmentIndex}`
  sourceId: string;              // bookingServiceId, `legacy-${bookingId}`, or tentative id
  bookingId: string;
  scheduledStart: string;
  scheduledEnd: string;
  serviceName: string;           // e.g. "Öljynvaihto"
  vehicleRegNumber: string;      // e.g. "ABC-123"
  /** Work order number for the tooltip and aria label (#3339); null on tentative items. */
  workOrderNumber?: string | null;
  vehicleMakeModel: string;
  customerName: string;
  customerPhone: string | null;
  assignedMechanicId: string | null;
  status: string;                // per-service status
  bookingStatus: BookingStatus;  // color and draggable logic
  liftName: string | null;
  customerWaits: boolean;
  jointInspectionRequested: boolean;
  durationHours: number;
  /** Full planned hours for the work item (segment duration may be shorter). */
  plannedHours?: number;
  actualDurationHours?: number;
  actualActiveHours?: number;
  mechanicName: string | null;
  isTentative?: boolean;
  /**
   * A day without a clock time (#2760). Spans the whole local day and is drawn
   * in that day's list, never as a timed, draggable block. `durationHours` is
   * still the work's hours, so utilization counts it on that day.
   */
  isDateOnly?: boolean;
  /** 'exact' = firm customer appointment, 'floating' = queued within the day (#1139). */
  schedulingType?: 'exact' | 'floating';
  segmentIndex: number;          // 0 for first/only segment, > 0 for shift-spanning continuations
  isContinuationSegment: boolean; // true when segmentIndex > 0 — not draggable
}

// Calendar event types
export interface CalendarEvent {
  id: string;
  type: 'inspection_completed' | 'approval_received' | 'customer_arrived' | 'vehicle_ready';
  time: string;
  mechanicId: string;
  bookingId?: string;
  description?: string;
}

// Digital Vehicle Inspection (DVI) types
export type InspectionStatus = 'draft' | 'pending' | 'in_progress' | 'completed' | 'sent_to_customer' | 'approved' | 'rejected';
export type InspectionItemCondition = 'good' | 'fair' | 'poor' | 'critical';

export interface Inspection {
  id: string;
  workOrderId: string;
  workOrderNumber: string;
  vehicleId: string;
  vehicle: Vehicle;
  customerId: string;
  customer: Customer;
  mechanicId: string;
  mechanic: Mechanic;
  status: InspectionStatus;
  mileage: number;
  categories: InspectionCategory[];
  notes?: string;
  recommendations?: string;
  totalRecommendedCents: number;
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
  sentAt?: string;
}

export interface InspectionCategory {
  id: string;
  name: string;
  icon?: string;
  items: InspectionItem[];
  order: number;
}

export interface MeasurementValueEntry {
  subItemId?: string;
  measurementId: string;
  value: number;
}

export interface MeasurementDefinition {
  id: string;
  label: string;
  unit: string;
  min?: number;
  warningMin?: number;
  max?: number;
  sortOrder: number;
}

export interface SubItemDefinition {
  id: string;
  label: string;
  sortOrder: number;
}

export interface InspectionItem {
  id: string;
  name: string;
  description?: string;
  categoryId: string; // Category this item belongs to (for cost estimation)
  condition: InspectionItemCondition | null;
  notes?: string;
  photos: InspectionPhoto[];
  recommendation?: InspectionRecommendation;
  order: number;
  // Measurement fields
  hasMeasurement?: boolean;
  measurementValue?: number | null;
  measurementUnit?: string;
  measurementMin?: number;
  measurementMax?: number;
  // Multi-measurement fields
  measurementValues?: MeasurementValueEntry[];
  subItemsConfig?: SubItemDefinition[] | null;
  measurementsConfig?: MeasurementDefinition[] | null;
}

export interface InspectionPhoto {
  id: string;
  url: string;
  thumbnailUrl: string;
  caption?: string;
  mediaType?: 'image' | 'video'; // Type of media
  duration?: number; // Duration in seconds for videos
  createdAt: string;
}

export interface InspectionRecommendation {
  id: string;
  description: string;
  urgency: 'immediate' | 'soon' | 'future';
  estimatedPriceCents: number;
  approved?: boolean;
  approvedAt?: string;
  workOrderItemId?: string | null; // Link to work order item
  linkedAt?: string; // When linked to work order
}

// Approval types
export type ApprovalStatus = 'pending' | 'approved' | 'rejected' | 'expired';
// API values first (approval_requests.approval_type, #2851); the four legacy
// portal-only values stay for mock data and older fixtures.
export type ApprovalType =
  | 'extra_work'
  | 'high_value'
  | 'customer_declined'
  | 'warranty_claim'
  | 'discount_request'
  | 'additional_work'
  | 'parts_replacement'
  | 'inspection_finding'
  | 'estimate_change';

export interface Approval {
  id: string;
  workOrderId: string;
  workOrderNumber: string;
  vehicleId: string;
  vehicle: Vehicle;
  customerId: string;
  customer: Customer;
  mechanicId: string;
  mechanic: Mechanic;
  type: ApprovalType;
  status: ApprovalStatus;
  title: string;
  description: string;
  items: ApprovalItem[];
  totalCents: number;
  originalEstimateCents?: number;
  photos: string[];
  urgency: 'immediate' | 'soon' | 'future';
  requestedAt: string;
  expiresAt?: string;
  respondedAt?: string;
  customerResponse?: string;
  sentVia: 'whatsapp' | 'sms' | 'email';
  messageId?: string;
}

export interface ApprovalItem {
  id: string;
  name: string;
  description?: string;
  priceCents: number;
  isRequired: boolean;
  approved?: boolean;
}

// Message/Conversation types
export type MessageChannel = 'whatsapp' | 'sms' | 'email' | 'internal' | 'portal';
export type MessageDirection = 'inbound' | 'outbound';
export type MessageStatus = 'sent' | 'delivered' | 'read' | 'failed';

export interface Message {
  id: string;
  conversationId: string;
  direction: MessageDirection;
  channel: MessageChannel;
  content: string;
  visibility?: 'customer' | 'internal';
  attachments?: MessageAttachment[];
  status: MessageStatus;
  sentAt: string;
  deliveredAt?: string;
  readAt?: string;
  senderName?: string; // For outbound: staff member name
  errorReason?: string; // For failed messages: delivery error description
  /** Workshop mailbox email (#3736); absent on every other message. */
  email?: EmailMessageDetail;
}

export type MailboxCategory = 'spam' | 'auto_reply' | 'bulk';

export interface EmailParty {
  address: string;
  name: string | null;
}

export interface EmailFile {
  id: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number | null;
}

/**
 * A stored mailbox email (#3736). `html` is the raw body: render it only
 * through `EmailHtmlFrame`, which sanitises it into a sandboxed iframe.
 */
export interface EmailMessageDetail {
  subject: string;
  from: EmailParty;
  to: EmailParty[];
  cc: EmailParty[];
  replyTo: string | null;
  date: string | null;
  html: string | null;
  htmlTruncated: boolean;
  textTruncated: boolean;
  rejectedAttachments: { name: string; reason: string }[];
  files: EmailFile[];
}

/** Thread facts of a channel='email' conversation; null on other channels. */
export interface ConversationEmail {
  address: string | null;
  subject: string | null;
  mailboxCategory: MailboxCategory | null;
  /** False while the sender matched no single customer. */
  linked: boolean;
}

export interface MessageAttachment {
  id: string;
  type: 'image' | 'document' | 'audio';
  url: string;
  thumbnailUrl?: string;
  fileName?: string;
  mimeType?: string;
}

/**
 * WhatsApp's 24-hour session window (#1471). Free-form replies are only allowed
 * while it is open; after that only approved templates go through. `null` on a
 * conversation means the channel has no such window (SMS, email, internal).
 */
export interface SessionWindow {
  active: boolean;
  lastInboundAt: string | null;
  expiresAt: string | null;
  minutesRemaining: number | null;
}

export interface WhatsAppTemplate {
  id: string;
  name: string;
  category: string;
  language: string;
  headerText: string | null;
  body: string;
  footer: string | null;
  /** How many `{{n}}` placeholders the body expects. */
  parameterCount: number;
}

export interface IntakeSummary {
  vehicleRegNumber?: string;
  vehicleMakeModel?: string;
  symptom?: string;
  urgency?: string;
  safetyNotes?: string;
  desiredTime?: string;
  proposedWorkType?: string;
  bookability?: string;
  clarifyingQuestions?: string[];
  workshopSummary?: string;
  updatedAt?: string;
}

export interface Conversation {
  id: string;
  customerId: string;
  customer: Customer;
  vehicleId?: string;
  vehicle?: Vehicle;
  workOrderId?: string;
  workOrderNumber?: string;
  channel: MessageChannel;
  lastMessage?: Message;
  unreadCount: number;
  isArchived: boolean;
  /** Customer asked for a person (#1596). Staff should reply in this thread. */
  requiresHuman?: boolean;
  customerPhase?: string;
  staffQueueStatus?: string;
  agentAuthority?: string;
  /** Structured intake for the workshop review card (#1584). */
  intakeSummary?: IntakeSummary;
  sessionWindow?: SessionWindow | null;
  email?: ConversationEmail | null;
  createdAt: string;
  updatedAt: string;
}

// Settings types
export type StaffRole = 'owner' | 'admin' | 'manager' | 'mechanic' | 'receptionist';

export interface StaffMember {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role: StaffRole;
  avatarUrl?: string;
  isActive: boolean;
  /** Counts toward workshop capacity (#3120); absent on older API builds. */
  isSchedulable?: boolean;
  skills?: string[];
  hourlyRateCents?: number;
  createdAt: string;
  lastLoginAt?: string;
}

export interface VatRate {
  id: string;
  label: string;
  percentage: number;
}

/**
 * 'exact' = firm customer appointment, 'floating' = queued within the day
 * (#1138/#1139). Named here because the workshop now stores a default for it
 * (#1931); the union is still written inline in older call sites.
 */
export type SchedulingType = 'exact' | 'floating';

/** Step for work-duration inputs, in minutes (#3091). */
export type WorkDurationStepMinutes = 15 | 30 | 60;

/**
 * Tenant `settings.features` flags. Every field is optional: an older
 * stored blob predates a key. Module visibility (#2646) treats a missing
 * key as on; WhatsApp and the other opt-in flags treat missing as off.
 */
export interface TenantFeatures {
  whatsappEnabled?: boolean;
  aiAgentsEnabled?: boolean;
  digitalInspectionEnabled?: boolean;
  calendarSyncEnabled?: boolean;
  inspectionsEnabled?: boolean;
  serviceBookEnabled?: boolean;
  partsOrderingEnabled?: boolean;
}

export interface WorkshopSettings {
  name: string;
  address: string;
  city: string;
  postalCode: string;
  country: string;
  phone: string;
  email: string;
  businessId: string; // Y-tunnus
  vatNumber?: string;
  openingHours: WorkshopOpeningHours;
  timezone: string;
  currency: string;
  laborRateCents: number;
  laborRateGrossCents?: number;
  vatRates: VatRate[];
  defaultVatRateId: string | null;
  schedulingLookaheadDays: number;
  /**
   * Scheduling type pre-selected for a new work order (#1931). Most work is
   * "during the day"; an exact time is the exception, so a workshop that has
   * never touched the setting gets `floating`.
   */
  defaultSchedulingType: SchedulingType;
  /** Workshop-wide default validity (days) for new quotations (#849). */
  defaultQuoteValidityDays: number;
  /** Unpaid lunch break (minutes) subtracted from each shift day's calendar capacity (#423). */
  workdayUnpaidLunchMinutes: number;
  smallSuppliesSurcharge?: SmallSuppliesSurcharge;
  /** Workshop-wide default (cents) for the work-order pre-approved additional-work limit (#296). */
  defaultAdditionalWorkLimitCents?: number;
  /** Annual late-payment interest (%) for business customers; seeded at provisioning, tenant-editable (#1752, #2890). */
  lateInterestRatePercent?: number | undefined;
  /** Annual late-payment interest (%) for consumer customers; seeded at provisioning, tenant-editable (#2890). */
  lateInterestRatePercentConsumer?: number | undefined;
  /**
   * Seller bank details printed on the invoice (#1035, #1036). An invoice
   * cannot be finalized for delivery without the IBAN, and until #1853 the
   * field had no editor anywhere in the portal.
   */
  bankIban?: string | undefined;
  bankBic?: string | undefined;
  /**
   * Below: fields the settings form edits that used to be cast in at the call
   * site (#2618). The cast kept them out of this type, so
   * WORKSHOP_SETTINGS_PAYLOAD_KEYS — a `Record<keyof WorkshopSettings, …>`
   * written to make a dropped field fail typecheck (#1795) — never demanded a
   * mapping, and the values silently never reached the server. Declaring them
   * here is the fix: the guard does the rest.
   */
  /** Extra reception questions asked when creating a work order (#2130). */
  additionalQuestions?: AdditionalQuestion[];
  /** Workshop's own links to external service books (#2129). */
  serviceBookLinks?: ServiceBookLink[];
  /** Minutes reserved after work before the customer collects (#2117). */
  defaultPickupBufferMinutes?: number;
  /** Step for work-duration sliders and arrows; reader default 30 (#3091). */
  workDurationStepMinutes?: WorkDurationStepMinutes;
}

/** A workshop-defined question shown at work-order create (#2130). */
export interface AdditionalQuestion {
  id: string;
  label: string;
  isActive: boolean;
}

/** A named link to an external service book (#2129). */
export interface ServiceBookLink {
  name: string;
  url: string;
  note?: string;
}

/** Service-book fill / override audit returned on a work-order detail (#2340). */
export interface WorkOrderServiceBookAudit {
  serviceBookFilledAt?: string | null;
  serviceBookFilledBy?: string | null;
  serviceBookOverrideReason?: string | null;
  serviceBookOverrideAt?: string | null;
  serviceBookOverrideBy?: string | null;
}

/**
 * A per-date override of the weekly opening hours — holiday, shortened day, or
 * an exceptional opening on a normally-closed day. Stored server-side, one row
 * per date, via `/api/v1/hours-exceptions` (#1282); no longer part of
 * `WorkshopSettings`.
 */
export interface HoursException {
  id: string;
  date: string;        // YYYY-MM-DD
  isClosed: boolean;
  open?: string;       // HH:mm
  close?: string;      // HH:mm
  reason?: string;
}

export interface WorkshopOpeningHours {
  monday: { open: string; close: string } | null;
  tuesday: { open: string; close: string } | null;
  wednesday: { open: string; close: string } | null;
  thursday: { open: string; close: string } | null;
  friday: { open: string; close: string } | null;
  saturday: { open: string; close: string } | null;
  sunday: { open: string; close: string } | null;
}

export interface NotificationSettings {
  emailNotifications: boolean;
  smsNotifications: boolean;
  pushNotifications: boolean;
  newBookingAlert: boolean;
  approvalReceivedAlert: boolean;
  customerMessageAlert: boolean;
  dailySummaryEmail: boolean;
  /** Email the customer when the vehicle is ready for pickup (#3121). */
  vehicleReadyCustomerEmail?: boolean;
  /** Which transition counts as ready (#3121). */
  vehicleReadyTrigger?: 'ready_to_invoice' | 'work_completed';
  /** WhatsApp the customer when the vehicle is ready; independent of the email switch (#3351). Off by default. */
  vehicleReadyCustomerWhatsApp?: boolean;
  /** Create a supervisor task when a customer answers the quote link (#3670). Off by default. */
  quoteDecisionTaskEnabled?: boolean;
}

// Inventory types
export type PartCategory = 'oil' | 'filters' | 'brakes' | 'tires' | 'electrical' | 'suspension' | 'engine' | 'body' | 'other';
export type StockStatus = 'in_stock' | 'low_stock' | 'out_of_stock' | 'on_order';

export interface Supplier {
  id: string;
  name: string;
  contactPerson?: string;
  phone?: string;
  email?: string;
  website?: string;
  deliveryDays: number; // Average delivery time in days
}

export interface InventoryItem {
  id: string;
  partNumber: string;
  name: string;
  description?: string;
  tags?: string[];
  category: PartCategory;
  /** Real part_categories.id — the field the API actually persists (#724). */
  categoryId?: string;
  categoryName?: string;
  brand?: string;
  /** Real parts.oem_part_number (#724). */
  oemPartNumber?: string;
  /** Real parts.unit — 'piece' | 'liter' | 'kg' | 'meter' | 'set' | 'pair' | 'box' (#724). */
  unit?: string;
  reorderQuantity?: number;
  isActive?: boolean;
  supplierId?: string;
  supplier?: Supplier;
  unitPriceCents: number;
  costPriceCents: number;
  quantity: number;
  minStockLevel: number;
  maxStockLevel?: number;
  location?: string; // Shelf/bin location
  barcode?: string;
  compatibleVehicles?: string[]; // e.g., ['Toyota Corolla 2018-2022', 'Toyota Camry 2019-2023']
  lastOrderedAt?: string;
  lastSoldAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface InventoryTransaction {
  warehouseName?: string;
  binLocationCode?: string;
  id: string;
  itemId: string;
  type: 'purchase' | 'sale' | 'adjustment' | 'return';
  quantity: number; // Positive for incoming, negative for outgoing
  unitPriceCents?: number;
  workOrderNumber?: string;
  notes?: string;
  createdAt: string;
  createdBy: string;
}

// Purchase Order types
// Matches the backend's PURCHASE_ORDER_STATUS_TRANSITIONS exactly (#726) —
// partially_received/received are set programmatically by the receive
// handler, never via a manual status transition.
export type PurchaseOrderStatus = 'draft' | 'approved' | 'ordered' | 'partially_received' | 'received' | 'cancelled';

export interface PurchaseOrder {
  id: string;
  orderNumber: string;
  supplierId: string;
  warehouseId: string;
  supplier: Supplier;
  status: PurchaseOrderStatus;
  items: PurchaseOrderItem[];
  subtotalCents: number;
  taxCents: number;
  shippingCents: number;
  totalCents: number;
  notes?: string;
  createdById: string;
  createdByName: string;
  approvedById?: string;
  approvedByName?: string | null;
  approvedAt?: string | null;
  orderedAt?: string;
  expectedDeliveryDate?: string;
  receivedAt?: string;
  // Supplier invoice matching ("laskutäsmäytys", #212).
  supplierInvoiceNumber?: string | null;
  supplierInvoiceDate?: string | null;
  supplierInvoiceTotalCents?: number | null;
  invoiceMatched: boolean;
  invoiceDiscrepancyCents?: number | null;
  invoiceMatchedAt?: string | null;
  invoiceMatchedByName?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PurchaseOrderItem {
  id: string;
  inventoryItemId?: string;
  partNumber: string;
  name: string;
  description?: string;
  quantity: number;
  unitCostCents: number;
  totalCostCents: number;
  receivedQuantity?: number;
  /** Booking id when the line is tied to a specific work order (transit warehouse). */
  linkedBookingId?: string | null;
  /** Human-readable work order context for the linked booking, if any. */
  linkedWorkOrder?: {
    bookingId: string;
    status: string | null;
    customerName: string | null;
    vehicleRegistration: string | null;
  } | null;
}

// Report types
export interface RevenueDataPoint {
  date: string;
  revenue: number;
  labor: number;
  parts: number;
  workOrders: number;
}

export interface MechanicProductivity {
  mechanicId: string;
  mechanicName: string;
  workOrdersCompleted: number;
  totalRevenue: number;
  laborHours: number;
  avgWorkOrderValue: number;
  efficiency: number; // Percentage
}

export interface ServiceTypeStats {
  serviceType: string;
  count: number;
  revenue: number;
  avgDuration: number;
}

export interface CustomerStats {
  newCustomers: number;
  returningCustomers: number;
  totalCustomers: number;
  avgVisitsPerCustomer: number;
  topCustomers: { name: string; revenue: number; visits: number }[];
}

export interface DashboardStats {
  totalRevenue: number;
  revenueChange: number; // Percentage change from previous period
  workOrdersCompleted: number;
  workOrdersChange: number;
  avgTicketValue: number;
  avgTicketChange: number;
  laborHours: number;
  laborHoursChange: number;
}

export interface CustomerSatisfactionData {
  overallScore: number; // 1-5 stars
  nps: number; // Net Promoter Score -100 to 100
  totalResponses: number;
  responseRate: number; // Percentage
  avgResponseTime: number; // Hours
  satisfactionChange: number; // Percentage change from previous period
  distribution: {
    excellent: number; // 5 stars
    good: number; // 4 stars
    average: number; // 3 stars
    poor: number; // 2 stars
    terrible: number; // 1 star
  };
  topComplaints: { issue: string; count: number }[];
  topPraises: { aspect: string; count: number }[];
}

export interface SatisfactionTrend {
  date: string;
  score: number; // Average satisfaction score
  responses: number;
  nps: number;
}

export interface AgentPerformanceData {
  totalInteractions: number;
  successfulResolutions: number;
  avgResponseTime: number; // Seconds
  avgResolutionTime: number; // Minutes
  handoffRate: number; // Percentage of interactions requiring human intervention
  intentAccuracy: number; // Percentage of correctly classified intents
  performanceChange: number; // Percentage change from previous period
  agentBreakdown: {
    agentType: string; // 'Customer', 'Mechanic', 'Manager', 'HR'
    interactions: number;
    successRate: number; // Percentage
    avgResponseTime: number; // Seconds
    handoffCount: number;
  }[];
  intentDistribution: {
    intent: string; // e.g., 'Booking', 'Status Inquiry', 'Approval', 'FAQ'
    count: number;
    successRate: number; // Percentage
  }[];
  topErrors: {
    error: string;
    count: number;
    agentType: string;
  }[];
}

export interface AgentPerformanceTrend {
  date: string;
  totalInteractions: number;
  successRate: number; // Percentage
  avgResponseTime: number; // Seconds
  handoffRate: number; // Percentage
}

// Time Tracking
export type TimeEntryType = 'clock_in' | 'clock_out' | 'break_start' | 'break_end' | 'lunch_start' | 'lunch_end';
export type ClockStatus = 'clocked_in' | 'on_break' | 'on_lunch' | 'clocked_out';

export interface TimeEntry {
  id: string;
  employeeId: string;
  employeeName: string | null;
  type: TimeEntryType;
  timestamp: string;
  note?: string | null;
  createdAt: string;
}

export interface ClockTime {
  employeeId: string;
  date: string;
  clockIn: string | null;
  clockOut: string | null;
}

export interface ClockSession {
  status: ClockStatus;
  clockInAt: string | null;
  currentEntryAt: string | null;
  todayTotalMinutes: number;
  todayBreakMinutes: number;
  todayLunchMinutes: number;
  entries: TimeEntry[];
}

/**
 * POST /hr/time-tracking/action: the session after the action plus the work
 * orders the break paused or the break end resumed (#3277).
 */
export interface ClockActionResult extends Omit<ClockSession, 'entries'> {
  entry: TimeEntry | null;
  pausedWorkOrderIds: string[];
  resumedWorkOrderIds: string[];
}

/**
 * Bill-to party on POST /invoices (#2427). Insurer is a company selected as payer.
 * A free invoice has no work order; do not invent a bookingId.
 */
export type InvoicePayerPartyType = 'person' | 'company' | 'insurer';

// Attachment types
export type AttachmentEntityType = 'booking' | 'vehicle' | 'customer' | 'inspection' | 'person' | 'company' | 'courtesy_loan';

export interface AttachmentEntityLink {
  workOrderNumber?: string | null;
  id: string;
  attachmentId: string;
  entityType: AttachmentEntityType;
  entityId: string;
  createdAt: string;
}

export interface Attachment {
  id: string;
  fileName: string;
  originalFileName: string;
  fileKey: string;
  fileUrl: string;
  thumbnailUrl: string | null;
  mimeType: string;
  fileSizeBytes: number | null;
  storageProvider: string;
  tags: string[];
  description: string | null;
  uploadedBy: string | null;
  createdAt: string;
  entityLinks: AttachmentEntityLink[];
  /** Null/omitted = staff upload; `inbound_email` = PF-136 mailbox. */
  source?: string | null;
}

/** PF-083 tenant policy for work-order customer approval. Code default is `never`. */
export type CustomerSignatureMode = 'never' | 'always' | 'conditional';

export interface CustomerSignatureSettings {
  mode: CustomerSignatureMode;
  thresholdGrossCents?: number;
  requireForConsumers?: boolean;
  allowBillingPartySigner?: boolean;
  allowDriverSigner?: boolean;
  consentText?: string;
  consentTextVersion?: number;
  /** Calendar date YYYY-MM-DD; work orders created before this day are exempt. */
  activeFrom?: string;
}

export type WorkOrderApprovalState = 'not_required' | 'pending' | 'valid' | 'lapsed';
export type WorkOrderSignerRole = 'orderer' | 'billing_party' | 'driver';
export type WorkOrderSignatureKind =
  | 'customer_signature'
  | 'paper_scan'
  | 'staff_attested_phone'
  | 'supervisor_override';
export type WorkOrderSignatureMethod =
  | 'counter_tablet'
  | 'remote_link'
  | 'portal'
  | 'paper'
  | 'phone'
  | 'override';
export type PhoneAttestationChannel =
  | 'phone'
  | 'in_person'
  | 'email'
  | 'sms'
  | 'whatsapp'
  | 'other';
export type OwnershipDocumentType =
  | 'id_card'
  | 'passport'
  | 'driving_licence'
  | 'company_authorization';
export type ApprovalLapseReason =
  | 'scope_added'
  | 'gross_increase_over_limit'
  | 'price_basis_changed';
export type SignaturePriceBasis = 'fixed' | 'estimate' | 'hourly';

export const WORK_ORDER_SIGNATURE_REQUIRED_CODE = 'WORK_ORDER_SIGNATURE_REQUIRED';

export interface WorkOrderSignatureScopeLine {
  key: string;
  kind: string;
  description: string;
  quantity: number;
  lineTotalCents: number;
}

export interface WorkOrderSignatureStatus {
  state: WorkOrderApprovalState;
  required: boolean;
  isSecurityJob: boolean;
  allowedSignerRoles: WorkOrderSignerRole[];
  settings: {
    mode: CustomerSignatureMode;
    thresholdGrossCents: number | null;
    requireForConsumers: boolean;
    allowBillingPartySigner: boolean;
    allowDriverSigner: boolean;
    consentText: string | null;
    consentTextVersion: number;
    activeFrom: string | null;
  };
  latestApproval: {
    id: string;
    kind: WorkOrderSignatureKind | string;
    method: WorkOrderSignatureMethod | string;
    signerName: string;
    signerRole: WorkOrderSignerRole | string;
    createdAt: string;
    scopeHash: string;
    isPhoneAttestation: boolean;
  } | null;
  latestOverride: {
    id: string;
    action: 'waive' | 'require';
    reason: string;
    createdAt: string;
  } | null;
  latestOwnershipVerification: {
    id: string;
    documentType: OwnershipDocumentType | string;
    matchesRegistry: boolean;
    createdAt: string;
  } | null;
  scope: {
    hash: string;
    grossTotalCents: number;
    priceBasis: SignaturePriceBasis | string;
    additionalWorkLimitCents: number;
    lines: WorkOrderSignatureScopeLine[];
  };
  lapse: {
    lapsed: boolean;
    reasons: ApprovalLapseReason[];
  } | null;
}
